'use client';
import { AutoHeightContainer } from '@/components/ui/auto-height-container';
import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { cn } from '@/lib/cn';
import { Urls } from '@/lib/urls';
import { SessionContext } from '@/providers/session-provider';
import { datadogRum } from '@datadog/browser-rum';
import { CornerDownLeftIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import {
  startTransition,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalErrorPage({ error, reset }: GlobalErrorProps) {
  // Global error replaces the entire layout, so SessionProvider may not be available.
  // We use useContext directly to avoid throwing if the provider is missing.
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.data ?? null;
  const router = useRouter();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<number | null>(null);
  const isGlobalErrorMessageEnabled = useFeatureFlag(
    FEATURE_FLAGS.GLOBAL_ERROR_MESSAGE
  );

  const handleCopy = async () => {
    if (copied) {
      return;
    }
    try {
      await navigator.clipboard.writeText(error.message);
      setCopied(true);
      if (copyResetRef.current) {
        clearTimeout(copyResetRef.current);
      }
      copyResetRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  function refresh() {
    startTransition(() => {
      reset();
      router.refresh();
    });
  }

  useEffect(() => {
    datadogRum.addError(error);
    return () => {
      if (copyResetRef.current) {
        clearTimeout(copyResetRef.current);
      }
    };
  }, [error]);

  return (
    <div className='flex h-svh items-center justify-center'>
      <Empty className='h-full'>
        <EmptyHeader>
          <EmptyMedia variant='icon'>
            <Icons.bug />
          </EmptyMedia>
          <EmptyTitle>Ett fel har uppstått</EmptyTitle>
          <EmptyDescription>
            Ett fel har uppstått när sidan laddades. Felet har loggats och våra
            utvecklare kommer att kolla på det.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <div className='flex gap-2'>
            {session?.user && (
              <Button
                variant='default'
                size='sm'
                onClick={() => {
                  router.push(Urls.dashboard.home);
                }}
              >
                Gå till dashboard
                <CornerDownLeftIcon className='ml-2 size-4' />
              </Button>
            )}
            <Button variant='outline' size='sm' onClick={refresh}>
              Försök igen
              <Icons.revert className='ml-2 size-4' />
            </Button>
          </div>
          {isGlobalErrorMessageEnabled && (
            <div className='flex w-full flex-col gap-2 rounded-xl border border-border bg-surface p-4'>
              <div className='flex items-center justify-between'>
                <div className='text-sm font-medium'>Felmeddelande</div>
                <div className='flex items-center gap-2'>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={() => setShowErrorMessage((prev) => !prev)}
                  >
                    <Icons.dropdown
                      className={cn(
                        'size-4 transition-transform',
                        showErrorMessage ? 'rotate-180' : ''
                      )}
                    />
                  </Button>
                  <Button variant='outline' size='icon' onClick={handleCopy}>
                    <div
                      className={cn(
                        'transition-all',
                        copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                      )}
                    >
                      <Icons.check
                        className='stroke-emerald-500'
                        size={16}
                        aria-hidden='true'
                      />
                    </div>
                    <div
                      className={cn(
                        'absolute transition-all',
                        copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
                      )}
                    >
                      <Icons.copy size={16} aria-hidden='true' />
                    </div>
                  </Button>
                </div>
              </div>
              <AutoHeightContainer>
                {showErrorMessage && (
                  <p className='max-h-40 overflow-y-auto text-left font-mono text-xs text-wrap tabular-nums'>
                    {error.message}
                  </p>
                )}
              </AutoHeightContainer>
            </div>
          )}
        </EmptyContent>
      </Empty>
    </div>
  );
}
